import * as React from 'react';
import {
  currentUrl,
  getDynamicData,
  historyBack,
  registerComponent,
  translate,
  useCurrentCountry,
  useMatchSmallViewport,
} from '@avensia/excite';
import {
  Block,
  Main,
  edgeMarginsDecorator,
  maxContentNarrowWidthDecorator,
  maxContentNormalWidthDecorator,
} from 'webapp/components/page-layout';
import { styled } from '@glitz/react';
import { Document } from '@avensia/excite-contentful';
import { BreadCrumbs } from 'webapp/components/Breadcrumbs';
import { isSvenssons, Theme } from 'webapp/theme';
import { createMinLargeViewportDecorator } from 'webapp/viewport';
import { addLineItem } from 'webapp/pages/checkout/commands';
import { useFlyout } from 'webapp/shell/flyout-context';
import { Button, FeedbackStandaloneButton } from 'webapp/components/button';
import { FieldSize } from 'webapp/components/field-size';
import { NnTestIdProps } from 'webapp/shared/test-ids';

export default registerComponent<NordicNest.Api.ProductConfigurationPageApiModel>(props => {
  const { open } = useFlyout('Cart');
  const country = useCurrentCountry();
  const isSmallViewport = useMatchSmallViewport();

  React.useEffect(() => {
    window.addEventListener('message', messageHandler, true); // use this if you are going to enable the cart button
  }, []);

  return (
    <Base>
      <BreadCrumbs {...props.breadcrumbs} />
      <ContentContainer>
        <Document {...props.page.content} />
      </ContentContainer>
      {isSmallViewport ? (
        <MobileTextWrapper>
          <Text>{props.page.mobileText}</Text>
        </MobileTextWrapper>
      ) : (
        <>
          {props.page.configurationIframeUrl && (
            <ConfigurationIframe
              scrolling="no"
              src={props.page.configurationIframeUrl
                ?.replace('{templateId}', currentUrl().searchParams.get('designtemplate') ?? '58764-1')
                .replace('{country}', country.twoLetterCode)}
              {...NnTestIdProps.ProductConfigurationIframe}
            />
          )}
          <FooterContainer>
            <Button
              as={Theme.Monochrome}
              size={isSvenssons ? FieldSize.Large : FieldSize.Default}
              onClick={() => historyBack()}
            >
              {translate(t => t.productConfigurationPageBackButton)}
            </Button>
            <AddToCartButton
              as={Theme.Positive}
              size={isSvenssons ? FieldSize.Large : FieldSize.Default}
              onClick={() =>
                addToCart().then(() => {
                  open();
                })
              }
              {...NnTestIdProps.ProductConfigurationAddToCartButton}
            >
              {translate(t => t.productConfigurationPageAddToCart)}
            </AddToCartButton>
          </FooterContainer>
        </>
      )}
    </Base>
  );
});

const Base = styled(Main, maxContentNormalWidthDecorator);

type Item = { ean: string; quantity: number; article: string };

function messageHandler(event: MessageEvent<any>) {
  if (event.data.event === 'designUpdate') {
    handleCart(event);
  }
}

let items: Item[];
function handleCart(event: MessageEvent<any>) {
  items = event.data.data.items;
}

async function addToCart() {
  const eans = items.map(i => i.ean);
  const context: NordicNest.Api.ProductSkusContext = { eans };
  const { skus } = await getDynamicData<NordicNest.Api.ProductSkusApiModel>(context).current();
  const promise = skus.map(product => {
    const item = items.find(i => i.ean === product.ean);
    if (item) {
      return addLineItem(product.sku, item.quantity, {
        eventData: {
          sku: product.sku,
          additionalProductAttributes: {
            ecommerce: { button_location: 'product_configuration_page', button_name: 'add_to_cart' },
          },
        },
      });
    }
  });

  return Promise.all(promise);
}

const FooterContainer = styled.div(edgeMarginsDecorator, {
  display: 'flex',
  justifyContent: 'flex-end',
  paddingBottom: t => (isSvenssons ? t.sharedSpacing.xxl : t.sharedSpacing.l),
});

const ContentContainer = styled(Block, maxContentNarrowWidthDecorator, edgeMarginsDecorator, {
  textAlign: 'center',
});

const AddToCartButton = styled(FeedbackStandaloneButton, {
  marginLeft: t => (isSvenssons ? t.sharedSpacing.l : t.sharedSpacing.s),
  width: 360,
});

const ConfigurationIframe = styled.iframe(
  edgeMarginsDecorator,
  createMinLargeViewportDecorator({
    height: 960,
  }),
  {
    width: '100%',
    height: 480,
  },
);

const MobileTextWrapper = styled.div(edgeMarginsDecorator, {
  backgroundColor: '#363636',
  color: 'rgba(255, 255, 255, 0.6)',
  textAlign: 'center',
  padding: { y: t => t.sharedSpacing.m },
  marginBottom: t => t.sharedSpacing.xl,
});

const Text = styled.div(maxContentNarrowWidthDecorator);
